import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import * as countryData from 'country-data';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs/Observable';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { CallingCountriesResponse, CountryCallingInfo } from './CountryCallingInfo';
import { Subject } from 'rxjs/Subject';
import { PricingNumbersService } from '../services/pricing-numbers.service';

@Component({
  selector: 'cc-countries-dropdown',
  templateUrl: './countries-dropdown.component.html',
  styleUrls: ['./countries-dropdown.component.scss']
})
export class CountriesDropdownComponent implements OnInit, OnDestroy {
  @Input() private initialCountryCode: string;
  @Input() private isFullyConfigured: boolean;
  @Output() change = new EventEmitter<CountryCallingInfo>();

  public formControl: FormControl;
  public countryFilter: FormControl = new FormControl();
  public filteredNumbers: Observable<CountryCallingInfo[]>;
  public currentNumber: CountryCallingInfo;
  private onDestroy = new Subject<void>();
  private countries: CountryCallingInfo[];
  private detectAutomaticallyItem: CountryCallingInfo = {
    countryCode: '',
    phonePrefix: '',
    flagEmoji: '',
    countryName: 'Detect automatically'
  };
  private availableCountries: CountryCallingInfo[];

  constructor(public pricingNumbersService: PricingNumbersService) {
    this.countries = [this.detectAutomaticallyItem, ...this.mapCountryData(countryData.callingCountries.all)];
  }

  ngOnInit() {
    this.getAvailableCountries();
    this.formControl = new FormControl();
    this.countryFilter.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.filter(this.countryFilter.value);
    });
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  private mapCountryData(countriesResponse: CallingCountriesResponse[]): CountryCallingInfo[] {
    return countriesResponse.map(country => ({
      countryCode: country.alpha2,
      phonePrefix: country.countryCallingCodes[0],
      flagEmoji: country.emoji,
      countryName: country.name
    }));
  }

  private findInitialCountry(): CountryCallingInfo {
    return this.initialCountryCode
      ? this.availableCountries.find(option => option.countryCode.toLowerCase() === this.initialCountryCode)
      : this.detectAutomaticallyItem;
  }

  filter(value: string | CountryCallingInfo) {
    if (this.isCountryCallingInfo(value)) {
      return;
    }
    const filterValue = value.toLowerCase();
    return this.availableCountries.filter(option => option.countryName.toLowerCase().includes(filterValue));
  }

  onSelectionChanged() {
    this.change.emit(this.currentNumber);
  }

  isCountryCallingInfo(val: any): val is CountryCallingInfo {
    return typeof val === 'object' && 'countryName' in val;
  }

  getAvailableCountries() {
    return this.pricingNumbersService.getAvailableCountries().subscribe(countries => {
      this.availableCountries = this.getCountries(countries);
      this.filteredNumbers = this.countryFilter.valueChanges.pipe(startWith(''), map(value => this.filter(value)));
      this.currentNumber = this.findInitialCountry();
      this.onSelectionChanged();
    });
  }

  getCountries(countries) {
    return countries.map(el => {
      const country = this.countries.find(e => e.countryCode.toLowerCase() === el.isoCountry.toLowerCase());
      return {
        countryName: country.countryName,
        countryCode: country.countryCode,
        phonePrefix: country.phonePrefix
      };
    });
  }
}
